import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent, Session, getUserAgents, getAgentTools } from '../../../lib/database';
import { useAuth } from '../../../contexts/AuthContext';

interface UnifiedSidebarProps {
  currentAgent: Agent | null;
  sessions: Session[];
  currentSessionId: string | null;
  isHistoryLoading: boolean;
  historyError: string | null;
  showMobileSidebar: boolean;
  onAgentSelect: (agentId: string) => void;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onCloseMobile: () => void;
}

// Custom Dropdown Component
interface AgentDropdownProps {
  agents: Agent[];
  selectedAgent: Agent | null;
  onAgentSelect: (agentId: string) => void;
  isLoading?: boolean;
  agentToolsCounts: Record<string, number>;
}

const AgentDropdown: React.FC<AgentDropdownProps> = ({
  agents,
  selectedAgent,
  onAgentSelect,
  isLoading,
  agentToolsCounts
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getAgentInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const handleAgentSelect = (agentId: string) => {
    onAgentSelect(agentId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-left flex items-center justify-between hover:border-gray-400 hover:shadow-sm transition-all duration-200 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${isOpen ? 'border-blue-500 ring-2 ring-blue-100' : ''}`}
      >
        {selectedAgent ? (
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 ${selectedAgent.color} rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
              {getAgentInitials(selectedAgent.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{selectedAgent.name}</div>
              {selectedAgent.description && (
                <div className="text-xs text-gray-500 truncate mt-0.5">{selectedAgent.description}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-sm text-gray-500 font-medium">
              {isLoading ? 'Loading agents...' : 'Choose an agent...'}
            </span>
          </div>
        )}

        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown Content */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-72 overflow-hidden">
            <div className="overflow-y-auto max-h-72">
              {agents.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-500 font-medium">No agents found</div>
                  <div className="text-xs text-gray-400 mt-1">Create your first agent to get started</div>
                </div>
              ) : (
                <div className="py-2">
                  {agents.map((agent, index) => (
                    <button
                      key={agent.id}
                      onClick={() => handleAgentSelect(agent.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-all duration-150 ${
                        selectedAgent?.id === agent.id
                          ? 'bg-blue-50 border-r-2 border-blue-500'
                          : ''
                      } ${index === 0 ? 'rounded-t-xl' : ''} ${index === agents.length - 1 ? 'rounded-b-xl' : ''}`}
                    >
                      <div className={`w-8 h-8 ${agent.color} rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm`}>
                        {getAgentInitials(agent.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${
                          selectedAgent?.id === agent.id ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {agent.name}
                        </div>
                        {agent.description && (
                          <div className="text-xs text-gray-500 truncate mt-0.5 leading-relaxed">
                            {agent.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {agent.model}
                          {agentToolsCounts[agent.id] !== undefined && (
                            <>
                              {' • '}
                              {agentToolsCounts[agent.id]} tool{agentToolsCounts[agent.id] !== 1 ? 's' : ''}
                            </>
                          )}
                        </div>
                      </div>
                      {selectedAgent?.id === agent.id && (
                        <div className="flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  currentAgent,
  sessions,
  currentSessionId,
  isHistoryLoading,
  historyError,
  showMobileSidebar,
  onAgentSelect,
  onSessionSelect,
  onNewChat,
  onCloseMobile,
}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [agentToolsCounts, setAgentToolsCounts] = useState<Record<string, number>>({});
  const [isAgentChanging, setIsAgentChanging] = useState(false);

  // Load all user agents
  useEffect(() => {
    const loadAgents = async () => {
      try {
        setIsLoadingAgents(true);
        const userAgents = await getUserAgents();
        setAgents(userAgents);

        // Load tools count for each agent
        const toolsCounts: Record<string, number> = {};
        await Promise.all(
          userAgents.map(async (agent) => {
            try {
              const tools = await getAgentTools(agent.id);
              toolsCounts[agent.id] = tools.length;
            } catch (error) {
              console.error(`Error loading tools for agent ${agent.id}:`, error);
              toolsCounts[agent.id] = 0;
            }
          })
        );
        setAgentToolsCounts(toolsCounts);
      } catch (error) {
        console.error('Error loading agents:', error);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    loadAgents();
  }, []);

  const handleEditAgent = () => {
    if (currentAgent) {
      navigate(`/edit/${currentAgent.id}`);
      onCloseMobile();
    }
  };

  const handleNewChatClick = () => {
    onNewChat();
    onCloseMobile();
  };

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect(sessionId);
    onCloseMobile();
  };

  const handleAgentSelectWrapper = (agentId: string) => {
    setIsAgentChanging(true);
    onAgentSelect(agentId);
    // Reset loading after a short delay to prevent UI flicker
    setTimeout(() => setIsAgentChanging(false), 1000);
    // Don't close mobile sidebar here - let user navigate through chats
    // Don't navigate URL here - let parent handle it without page refresh
  };

  const handleCreateAgent = () => {
    navigate('/create');
    onCloseMobile();
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const getUserInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const sidebarContent = (
    <>
      {/* Header - Agent Selection */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Select Agent
          </label>
          <AgentDropdown
            agents={agents}
            selectedAgent={currentAgent}
            onAgentSelect={handleAgentSelectWrapper}
            isLoading={isLoadingAgents || isAgentChanging}
            agentToolsCounts={agentToolsCounts}
          />
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={handleCreateAgent}
            className="w-full flex items-center space-x-3 p-2 text-left hover:bg-white hover:shadow-sm rounded-lg transition-all text-sm border border-gray-200 bg-white"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <span className="text-gray-700 font-medium">Create Agent</span>
          </button>

          {currentAgent && (
            <>
              <button
                onClick={handleEditAgent}
                className="w-full flex items-center space-x-3 p-2 text-left hover:bg-white hover:shadow-sm rounded-lg transition-all text-sm"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                <span className="text-gray-700 font-medium">Edit Agent</span>
              </button>

              <button
                onClick={handleNewChatClick}
                className="w-full flex items-center space-x-3 p-2 text-left hover:bg-white hover:shadow-sm rounded-lg transition-all text-sm"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                <span className="text-gray-700 font-medium">New Chat</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Chat Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {currentAgent ? (
          <>
            {isHistoryLoading && (
              <div className="p-4 text-sm text-center text-gray-500">Loading chats...</div>
            )}
            {historyError && (
              <div className="p-4 text-sm text-center text-red-500">{historyError}</div>
            )}
            {!isHistoryLoading && !historyError && sessions.length === 0 && (
              <div className="p-4 text-sm text-center text-gray-500">
                No chats found.
                <br />
                <button
                  onClick={handleNewChatClick}
                  className="text-blue-600 hover:text-blue-700 underline mt-1"
                >
                  Start your first chat
                </button>
              </div>
            )}

            <div className="px-2 py-2">
              {sessions.map((session, index) => (
                <button
                  key={session.id}
                  onClick={() => handleSessionClick(session.id)}
                  className={`w-full text-left p-3 rounded-lg mb-2 transition-all hover:bg-gray-50 ${
                    currentSessionId === session.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        Chat {sessions.length - index}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {new Date(session.update_time).toLocaleDateString()} at {new Date(session.update_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="p-4 text-sm text-center text-gray-500">
            Select an agent to view chats
          </div>
        )}
      </div>

      {/* User Profile Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-netlify-teal rounded-lg flex items-center justify-center flex-shrink-0">
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt="Profile"
                className="w-8 h-8 rounded-lg object-cover"
              />
            ) : (
              <span className="text-sm font-bold text-white">
                {user ? getUserInitials(user.user_metadata?.full_name || user.email || 'U') : 'U'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {user?.user_metadata?.full_name || 'User'}
            </div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleSignOut}
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            title="Sign out"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Sidebar - Overlay */}
      <div className="lg:hidden">
        {showMobileSidebar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCloseMobile} />
        )}
        <div className={`fixed top-0 left-0 bottom-0 w-80 bg-white transform transition-transform duration-300 z-50 flex flex-col ${
          showMobileSidebar ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {sidebarContent}
        </div>
      </div>

      {/* Desktop Sidebar - Fixed */}
      <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 flex-col">
        {sidebarContent}
      </div>
    </>
  );
};

export default UnifiedSidebar;